import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { isNative } from "@/lib/native";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CreditCard, Loader2, ExternalLink, XCircle, RotateCcw, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

const TIER_LABEL = { support: "Ongoing Support", starter: "Starter Support", professional: "Professional Manager", enterprise: "Enterprise Multi-site" };

export default function ManageBilling({ onChanged }) {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const yearly = (user?.subscription_lookup_key || "").endsWith("_yearly");

  useEffect(() => { if (open) api.get("/plans").then((r) => setPlans(r.data)); }, [open]);

  const act = async (key, fn) => {
    setBusy(key);
    try {
      const data = await fn();
      if (data) { setUser(data); onChanged?.(); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const portal = () => act("portal", async () => {
    const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
    window.location.href = data.url;
  });
  const termEnd = user?.subscription_commitment_until ? new Date(user.subscription_commitment_until).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;
  const cancel = () => {
    const warning = user?.founder
      ? `Cancelling now forfeits your Founders Club discount and your numbered place — permanently.${termEnd ? ` Your plan ends at the end of the current paid period.` : ""} Continue?`
      : termEnd ? `You're inside your 3-month minimum paid term, which remains payable until ${termEnd}. Your plan will end on that date. Continue?` : "Cancel at the end of the current paid period?";
    if (!window.confirm(warning)) return;
    act("cancel", async () => { const { data } = await api.post("/payments/subscription/cancel"); toast.success(user?.founder ? "Plan ending — Founders Club place released" : "Your plan will end as scheduled"); return data; });
  };
  const resume = () => act("resume", async () => { const { data } = await api.post("/payments/subscription/resume"); toast.success("Your plan will continue"); return data; });
  const change = (lookup) => act(lookup, async () => { const { data } = await api.post("/payments/subscription/change-plan", { lookup_key: lookup }); toast.success(`Switched to ${TIER_LABEL[data.subscription_tier]}`); return data; });

  if (!user?.has_billing || isNative) return null;

  return (
    <>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => setOpen(true)} data-testid="manage-billing-button">
        <CreditCard className="w-4 h-4 mr-1.5" /> Manage billing
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="manage-billing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Manage your subscription</DialogTitle>
            <DialogDescription>
              {TIER_LABEL[user.subscription_tier]} · {yearly ? "yearly" : "monthly"}
              {user.subscription_period_end && ` · ${user.cancel_at_period_end ? "ends" : "renews"} ${new Date(user.subscription_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><ArrowUpDown className="w-4 h-4" /> Switch plan</p>
              <div className="grid gap-2">
                {plans.map((p) => {
                  const lookup = yearly ? p.yearly_lookup : p.monthly_lookup;
                  const current = p.id === user.subscription_tier;
                  return (
                    <div key={p.id} className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${current ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`billing-plan-${p.id}`}>
                      <div>
                        <div className="text-sm font-medium flex items-center gap-1.5">{p.name} {current && <Badge variant="secondary" className="text-[10px]">Current</Badge>}</div>
                        <p className="text-xs text-muted-foreground">£{yearly ? p.yearly_price : p.monthly_price}/{yearly ? "yr" : "mo"} · prorated</p>
                      </div>
                      {!current && (
                        <Button size="sm" variant="outline" onClick={() => change(lookup)} disabled={!!busy} data-testid={`billing-switch-${p.id}`}>
                          {busy === lookup ? <Loader2 className="w-4 h-4 animate-spin" /> : "Switch"}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
              <Button variant="outline" size="sm" onClick={portal} disabled={!!busy} data-testid="billing-portal-button">
                {busy === "portal" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ExternalLink className="w-4 h-4 mr-1.5" />} Update card & invoices
              </Button>
              {user.cancel_at_period_end ? (
                <Button variant="secondary" size="sm" onClick={resume} disabled={!!busy} data-testid="billing-resume-button">
                  {busy === "resume" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RotateCcw className="w-4 h-4 mr-1.5" />} Keep my plan
                </Button>
              ) : (
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={cancel} disabled={!!busy} data-testid="billing-cancel-button">
                  {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <XCircle className="w-4 h-4 mr-1.5" />} Cancel {termEnd && !user.founder ? "(term payable)" : "at period end"}
                </Button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground" data-testid="billing-cancel-route">Cancelling is as easy as signing up: two clicks here, or email faye@futuregenservices.com — no phone call or notice period. Your plan auto-renews at the same price until you cancel. If a payment fails we retry and email you for 14 days before the plan ends.</p>
            {termEnd && !user.cancel_at_period_end && (
              <p className="text-xs text-muted-foreground" data-testid="billing-min-term-note">
                {user.founder ? `Founders Club: 12-month minimum term to ${termEnd}. Cancelling before then forfeits your 50% discount and your place.` : `3-month minimum paid term to ${termEnd} — if you cancel before then the term remains payable.`}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
